/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * User Stories Operations
 * 
 * This module handles user story-related API operations including:
 * - Fetching user stories markdown content
 */

import { buildProductApiUrl } from './base';
import type {
  UserStoriesMarkdownResult,
} from '../../../server/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Context for user story API calls
 */
export interface UserStoryRepoContext {
  repoUrl?: string | null;
  repoPath?: string | null;
}

// ============================================================================
// User Story Operations
// ============================================================================

/**
 * Fetch user stories markdown content
 * 
 * @param repoContext - Repository context for the product
 * @returns Markdown content string
 */
export async function getUserStoriesMarkdown(
  repoContext?: UserStoryRepoContext
): Promise<UserStoriesMarkdownResult> {
  try {
    const params: Record<string, string> = {};
    if (repoContext?.repoUrl) {
      params.repoUrl = repoContext.repoUrl;
    }
    if (repoContext?.repoPath) {
      params.repoPath = repoContext.repoPath;
    }
    
    const apiUrl = buildProductApiUrl('/api/data/user-stories', params);

    const response = await fetch(apiUrl);

    if (!response.ok) {
      if (response.status === 404) {
        // 404 means directory/files don't exist yet
        return {
          success: true,
          content: ''
        };
      }
      return {
        success: false,
        error: `Failed to fetch user stories (${response.status})`
      };
    }

    const content = await response.text();
    return {
      success: true,
      content
    };
  } catch (error: any) {
    console.error('[Product API] Error fetching user stories markdown:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch user stories'
    };
  }
}

